import { alpha } from '@mui/material'
import { css, Interpolation, Theme } from '@emotion/react'
import { QuestionInvertTypeEnum } from '__generatedTypes__/globalTypes'

export const DATA_ATTRIBUTE = 'data-invert-for-dark-mode'

export const wrapper: Interpolation<Theme> = (theme) => css`
  margin: ${theme.spacing(1, 0, 3)};
`

export const title: Interpolation<Theme> = (theme) => css`
  font-weight: 400;
  font-size: 16px;
  line-height: 20px;
  color: ${theme.typographyColors.typographyThree};
  margin-right: 10px;
`

export const select: Interpolation<Theme> = (theme) => css`
  width: 100px;
  display: inline-block;
  margin-right: 6px;

  .MuiButtonBase-root {
    height: ${theme.spacing(4)};
  }
`

export const icon: Interpolation<Theme> = (theme) => css`
  height: 20px;
  width: 20px;
  color: ${theme.typographyColors.typographyThree};
`

export const modal: Interpolation<Theme> = (theme) => css`
  position: absolute;
  top: 50%;
  left: 50%;
  transform: translate(-50%, -50%);
  display: flex;
  flex-direction: column;
  justify-content: flex-start;
  align-items: center;
  width: 900px;
  max-width: calc(100% - 20px);
  max-height: calc(100% - 20px);
  gap: 15px;
  padding: 22px;
  background: ${theme.backgrounds.backgroundFour};
  border: 1px solid ${theme.backgrounds.backgroundOne};
  box-shadow: ${theme.boxShadows.medium};
  border-radius: 24px;
  box-sizing: border-box;
  overflow: hidden;
`

export const modalTitle: Interpolation<Theme> = (theme) => css`
  font-weight: 600;
  font-size: 18px;
  line-height: 24px;
  color: ${theme.typographyColors.typographyOne};
  text-align: center;
  flex-shrink: 0;
`

export const content: Interpolation<Theme> = css`
  width: 100%;
  overflow-y: auto;
  display: flex;
  flex-direction: column;
  gap: 20px;
  flex: 1;
  min-height: 0;
`

export const previewContainer: Interpolation<Theme> = css`
  display: flex;
  flex-direction: column;
  gap: 8px;
  width: 100%;
`

export const previewLabel: Interpolation<Theme> = (theme) => css`
  font-weight: 600;
  font-size: 14px;
  line-height: 18px;
  color: ${theme.typographyColors.typographyTwo};
  text-transform: uppercase;
  letter-spacing: 0.5px;
`

export const htmlPreview: Interpolation<Theme> = css`
  padding: 16px;
  border-radius: 8px;
  width: 100%;
  box-sizing: border-box;
  overflow-x: auto;

  img {
    max-width: 100%;
    height: auto;
  }
`

export const imagesWrapper: Interpolation<Theme> = (theme) => css`
  aspect-ratio: 2/1;
  width: 600px;
  max-width: 100%;
  display: grid;
  grid-template-columns: 1fr 1fr;
  margin-bottom: 10px;

  :last-child {
    margin-bottom: 0;
  }

  div {
    display: flex;
    align-items: center;
    justify-content: center;
    padding: 30px;

    img {
      max-height: 100%;
      max-width: 100%;
    }

    ${theme.breakpoints.down(650)} {
      padding: 10px;
    }
  }

  .light {
    background: ${theme.colors.white};
  }

  .dark {
    background: ${theme.colors.black};
  }
`

const getHtmlBaseStyles = (theme: Theme) => css`
  overflow-wrap: anywhere;
  * {
    max-width: 100%;
  }
  a {
    color: ${theme.colors.blue};
    text-decoration: none;
    cursor: pointer;
    transition: color 0.2s ease-in-out;
    &:hover {
      color: #1a6fcc;
    }
  }
  sup {
    display: inline-block;
    margin-top: -10px;
  }
  sub {
    display: inline-block;
    margin-bottom: -10px;
  }
  & > :first-child {
    margin-top: 0;
  }
  & > :last-child {
    margin-bottom: 0;
  }
  .ql-align-right {
    text-align: right;
  }
  .ql-align-center {
    text-align: center;
  }
  .ql-align-justify {
    text-align: justify;
  }
  font-size: 14px;
  line-height: 20px;
  .text-tiny {
    font-size: 10px !important;
    line-height: 14px !important;
  }
  .text-small {
    font-size: 12px !important;
    line-height: 16px !important;
  }
  .text-big {
    font-size: 16px !important;
    line-height: 28px !important;
  }
  .text-huge {
    font-size: 18px !important;
    line-height: 34px !important;
  }
  .text-blog-title {
    font-size: 22px !important;
    line-height: 34px !important;
  }
  .text-subtitle {
    font-weight: 700 !important;
  }
  .bar {
    display: inline-block;
    width: 100%;
    padding: 11px;
    text-align: center;
    background: #f6f6f7;
    color: #323b4b;
    font-weight: 700;
    border-radius: 8px;
    font-size: 16px;
    line-height: 19px;
  }
  .preview-image-link {
    color: ${theme.colors.blue};
    text-decoration: underline;
    text-decoration-style: solid;
    text-underline-offset: 2px;
    cursor: pointer;
    transition: color 0.2s ease-in-out;
    &:hover {
      color: #1a6fcc;
    }
  }
  figure {
    margin-left: auto;
    margin-right: auto;
    width: fit-content;
    min-width: 250px;
    height: auto !important;
    figcaption {
      font-size: 12px;
      text-align: center;
    }
    &.image-style-side {
      margin-right: 0;
    }
  }
  figure figure {
    min-width: auto;
  }
  .image_resized img {
    width: 100%;
  }
  // youtube or vimeo video wrapper
  figure:has(div[data-oembed-url]) {
    width: 100%;

    div[data-oembed-url] {
      width: 100%;
    }
  }
  // math and chemistry formulas
  math {
    font-family: ${theme.fonts.family.CambriaMath};
  }
  // standalone formula paragraphs: scroll instead of clipping on small screens
  p:has(> math:only-child) {
    max-width: 100%;
    overflow-y: hidden;
    overflow-x: auto;
    > math:only-child {
      max-width: none; // override * { max-width: 100% }
    }
  }
  .todo-list {
    list-style: none;
    li {
      position: relative;
      margin-bottom: 5px;
      input {
        position: absolute;
        left: -25px;
      }
    }
  }
  table {
    height: 100%;
    width: 100%;
    border-collapse: collapse;
    border-spacing: 0;
  }
  blockquote {
    border-left: 5px solid #ccc;
    font-style: italic;
    margin-left: 0;
    margin-right: 0;
    overflow: hidden;
    padding-left: 1.5em;
    padding-right: 1.5em;
  }
  pre {
    display: block;
    font-family: monospace;
    margin: 1em 0;
    background: hsla(0, 0%, 78%, 0.3);
    border: 1px solid #c4c4c4;
    border-radius: 2px;
    color: #353535;
    direction: ltr;
    font-style: normal;
    min-width: 200px;
    padding: 1em;
    tab-size: 4;
    text-align: left;
    white-space: pre-wrap;
  }
  hr {
    border: none;
    height: 1px;
    background-color: #d4d8de;
  }
`

const LIGHT_MODE_COLORS = {
  typographyPrimary: '#09090B',
  background: '#FFF',
}

const DARK_MODE_COLORS = {
  typographyPrimary: '#ffffff',
  typographySecondary: '#9a9a9a',
  background: '#111111',
}

export const lightModePreviewStyles: Interpolation<Theme> = (theme) => css`
  ${getHtmlBaseStyles(theme)};
  color: ${LIGHT_MODE_COLORS.typographyPrimary};
  font-weight: 400;
  font-size: 16px;
  line-height: 24px;
  background: ${LIGHT_MODE_COLORS.background};
`

export const darkModePreviewStyles: Interpolation<Theme> = (theme) => css`
  ${getHtmlBaseStyles(theme)};
  color: ${DARK_MODE_COLORS.typographySecondary};
  font-weight: 400;
  font-size: 16px;
  line-height: 24px;
  background: ${DARK_MODE_COLORS.background};

  .bar {
    color: ${DARK_MODE_COLORS.typographyPrimary};
    background: ${alpha(DARK_MODE_COLORS.typographyPrimary, 0.1)};
    border: 1px solid ${alpha(DARK_MODE_COLORS.typographyPrimary, 0.1)};
    padding: 10px;
  }

  img[${DATA_ATTRIBUTE}='${QuestionInvertTypeEnum.YES}'] {
    filter: invert(100%);
  }

  img[${DATA_ATTRIBUTE}='${QuestionInvertTypeEnum.NO}'] {
    background: ${theme.colors.white};
  }
`

export const imagePopover =
  (isDarkMode?: boolean, invertMode?: string): Interpolation<Theme> =>
  (theme) =>
    css`
      max-width: 600px;
      max-height: 600px;
      display: flex;
      align-items: center;
      justify-content: center;
      padding: 16px;
      background: ${isDarkMode ? DARK_MODE_COLORS.background : LIGHT_MODE_COLORS.background};
      border: 1px solid ${isDarkMode ? DARK_MODE_COLORS.typographySecondary : LIGHT_MODE_COLORS.typographyPrimary};
      border-radius: 16px;

      img {
        max-width: 100%;
        max-height: 100%;
        width: auto;
        height: auto;
        display: block;
        ${isDarkMode && invertMode === QuestionInvertTypeEnum.YES && 'filter: invert(100%);'}
        ${isDarkMode && invertMode === QuestionInvertTypeEnum.NO && `background: ${theme.colors.white};`}
      }
    `
